import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { WHATSAPP_URL } from "../lib/constants";
import { usePageMeta } from "../lib/usePageMeta";
import { api } from "../lib/api";
import { CorporateQuoteModal, MarcasBlock } from "./Portfolio";
import { ESCOLAS_LOGOS } from "../components/LogoMarquee";
import "./Portfolio.css";
import {
  Briefcase, Bus, Building2, Mic, Users, ShieldCheck, FileCheck2, Sparkles,
  Award, Handshake, Calendar, Rocket, ArrowRight, ClipboardList, Plane,
  LineChart, TrendingDown, Clock, HelpCircle,
} from "lucide-react";

const HERO_IMG = "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/6trel8lm_Garcia-2.jpg";
const BUS_IMG = "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/6trel8lm_Garcia-2.jpg";
const BUS_INTERIOR_IMG = "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/ngpyecc4_images%20%284%29.jpg";
const HOTEL_IMG = "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=900&q=70";
const EVENT_IMG = "https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=900&q=70";

const PILLARS = [
  {
    icon: Bus,
    title: "Turismo com Ônibus-Leito no Padrão Premium",
    body: "Frota parceira com ônibus-leito premium para grupos, deslocamentos regionais e roteiros de médio curso — Wi-Fi Starlink a bordo e conforto executivo em cada trecho.",
  },
  {
    icon: Building2,
    title: "Hotéis no Padrão Corporativo",
    body: "Selecionamos hotéis com estrutura para eventos: salas de reunião, coffee break, internet de alta velocidade e diárias negociadas.",
  },
  {
    icon: Mic,
    title: "Palestras & Workshops",
    body: "Integração com palestrantes motivacionais, mentores e facilitadores. Do team building externo ao workshop interno com programação técnica.",
  },
  {
    icon: Plane,
    title: "Emissão & Logística Aérea",
    body: "Passagens aéreas nacionais e internacionais com atendimento dedicado, remarcações ágeis, cortesias e opções de classe executiva.",
  },
  {
    icon: Users,
    title: "Grupos & Convenções",
    body: "Da reunião regional de 20 pessoas ao evento nacional de 500. Coordenamos hospedagem, transfer, alimentação e branding do evento.",
  },
  {
    icon: Calendar,
    title: "Feiras, Congressos e Roadshows",
    body: "Pacotes completos para participação em feiras nacionais e internacionais: aéreo, hospedagem, credenciais e transfer VIP.",
  },
];

const FAQ = [
  {
    q: "Por que contratar uma agência para as viagens corporativas da minha empresa?",
    a: "Uma consultoria dedicada consolida negociações com fornecedores (hotéis, cias aéreas, ônibus), reduz custos operacionais entre 15-30%, elimina o tempo do time interno pesquisando cotações e garante suporte 24/7 durante toda a viagem. Sua equipe foca no negócio, a Orion cuida do resto.",
  },
  {
    q: "Como funciona o modelo de cobrança e a nota fiscal?",
    a: "Emitimos contrato formal por evento ou por acordo anual. Você recebe prestação de contas completa com todos os receipts.",
  },
  {
    q: "Vocês atendem apenas empresas grandes?",
    a: "Não. Atendemos desde deslocamentos pontuais de 2 executivos até convenções de 500 pessoas. A abordagem é a mesma: entender o objetivo, propor o melhor caminho e coordenar 100% da execução.",
  },
  {
    q: "Quanto tempo de antecedência preciso solicitar?",
    a: "Ideal 10-15 dias para viagens simples e 30-60 dias para eventos com grupos ou destinos internacionais. Para emergências (deslocamentos urgentes), atendemos em até 4 horas úteis com opções.",
  },
  {
    q: "A Viagens Orion é regularizada?",
    a: "Sim. CNPJ 37.476.039/0001-90 (SR SCABIO OPERADORA), Cadastur ativo no Ministério do Turismo, contrato formal CDC-compliant e emissão de nota fiscal sobre serviço/comissão. Empresa 100% regularizada.",
  },
  {
    q: "Tem dashboard para acompanhar as viagens?",
    a: "Sim. Após a contratação, disponibilizamos um dashboard exclusivo com relatórios de gastos, próximas viagens, aprovações pendentes e KPIs de compliance — atualizado em tempo real.",
  },
];

const HOW = [
  { step: "01", title: "Briefing", body: "Reunião presencial ou remota para entender objetivos do evento, verba, agenda e perfil dos participantes." },
  { step: "02", title: "Proposta", body: "Roteiro detalhado com opções de fornecedores, valores fechados e cronograma. Sem custos ocultos." },
  { step: "03", title: "Execução", body: "Coordenação 24/7 durante toda a viagem — antes, durante e no pós. Um consultor dedicado responde no seu WhatsApp." },
  { step: "04", title: "Prestação de Contas", body: "Emissão de contrato, notas fiscais dos serviços e relatório final com receipts. Auditável do início ao fim." },
];

const TRUST = [
  { icon: ShieldCheck, title: "Cadastur ativo", body: "Registro oficial no Ministério do Turismo." },
  { icon: FileCheck2, title: "Contrato formal", body: "Termos claros, CDC-compliant, com todas as garantias." },
  { icon: ClipboardList, title: "Notas fiscais", body: "Emissão da NF sobre a comissão / serviço da agência, com prestação de contas." },
  { icon: LineChart, title: "Dashboard exclusivo", body: "Relatórios em tempo real de gastos, próximas viagens e compliance." },
];

export default function Corporativo() {
  usePageMeta(
    "Viagens Corporativas · Excelência e Economia · Viagens Orion",
    "Viagens corporativas com excelência e economia: consultoria dedicada, ônibus-leito premium com Wi-Fi Starlink, hotéis corporativos, palestras motivacionais, dashboard exclusivo. Empresa Cadastur com contrato e NF.",
    { canonical: "https://viagensorion.com.br/agencia-de-viagens-corporativa" }
  );

  const [openFaq, setOpenFaq] = React.useState(0);
  const [quoteOpen, setQuoteOpen] = React.useState(false);
  const [destinations, setDestinations] = React.useState([]);
  React.useEffect(() => {
    api.get("/portfolio").then((r) => {
      const secs = r.data?.sections || [];
      const map = new Map();
      for (const s of secs) {
        for (const it of s.items) if (!map.has(it.id)) map.set(it.id, it);
      }
      setDestinations(Array.from(map.values()));
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1" data-testid="corporativo-page">
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#2C1139] via-[#3d1b52] to-[#0d0413] text-white">
          <span className="absolute inset-0 opacity-25" aria-hidden style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }} />
          <span className="absolute inset-0 bg-gradient-to-b from-[#2C1139]/95 via-[#2C1139]/75 to-[#0d0413]/85" aria-hidden />
          <span className="absolute -top-40 -right-32 w-[500px] h-[500px] rounded-full bg-orion-orange/25 blur-3xl" aria-hidden />

          <div className="relative container-orion py-16 md:py-28 max-w-4xl text-center mx-auto">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-5">
              <Briefcase size={13} /> Viagens Orion Corporativo
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] mx-auto">
              <span className="text-orion-orange">Excelência e economia</span> nas viagens de trabalho da sua empresa.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/85 mx-auto leading-relaxed">
              Somos a agência de <strong>viagens corporativas</strong> que reduz seu custo médio por viagem em <strong>até 30%</strong> sem abrir mão de conforto — com ônibus-leito premium Wi-Fi Starlink, hotéis executivos, dashboard em tempo real, contrato formal, nota fiscal e Cadastur ativo.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setQuoteOpen(true)}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-orion-gold via-[#FFC470] to-[#38F0FF] text-[#2C1139] px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:brightness-110 transition-all shadow-[0_14px_38px_rgba(255,179,71,0.45)] hover:-translate-y-0.5"
                data-testid="corp-cta-quote"
              >
                Solicitar Cotação Corporativa <ArrowRight size={14} />
              </button>
              <Link
                to="/viagens-corporativas/portfolio"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/30 text-white px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-white/20 hover:border-orion-gold transition-all"
                data-testid="corp-cta-portfolio"
              >
                <Sparkles size={14} /> Experiências Corporativas
              </Link>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:border-orion-orange hover:text-orion-orange transition-colors"
                data-testid="corp-cta-wa"
              >
                <WhatsAppIcon size={16} color="#ffffff" /> WhatsApp
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4 md:gap-8 text-[10px] uppercase tracking-[0.3em] text-white/75">
              <span className="inline-flex items-center gap-2"><ShieldCheck size={12} className="text-orion-gold" /> Cadastur ativo</span>
              <span className="inline-flex items-center gap-2"><FileCheck2 size={12} className="text-orion-gold" /> Contrato formal</span>
              <span className="inline-flex items-center gap-2"><ClipboardList size={12} className="text-orion-gold" /> Nota fiscal</span>
              <span className="inline-flex items-center gap-2"><LineChart size={12} className="text-orion-gold" /> Dashboard exclusivo</span>
              <span className="inline-flex items-center gap-2"><Award size={12} className="text-orion-gold" /> Empresa regularizada</span>
            </div>
          </div>
        </section>

        {/* TRUST STRIP */}
        <section className="border-y border-[#EFEAE0] bg-[#FAF7F0]">
          <div className="container-orion py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {TRUST.map((t, i) => (
              <div key={i} className="flex flex-col items-center gap-2" data-testid={`corp-trust-${i}`}>
                <t.icon size={22} className="text-orion-orange" />
                <div className="font-serif text-orion-purple text-sm md:text-base">{t.title}</div>
                <div className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-[#594A63] leading-snug">{t.body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* PILLARS / SERVICES */}
        <section id="servicos" className="container-orion py-20 md:py-28">
          <div className="max-w-2xl mx-auto mb-14 text-center">
            <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">O que fazemos</div>
            <h2 className="font-serif text-3xl md:text-5xl text-orion-purple leading-tight">
              Um ecossistema completo para viagens executivas.
            </h2>
            <p className="mt-4 text-[#594A63] text-base md:text-lg leading-relaxed">
              Somos a extensão do seu RH e do seu comercial. Nossa equipe atua com atendimento consultivo, coordenando cada peça — do transfer da equipe ao closing dinner com clientes VIPs.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <div
                key={i}
                className="group border border-[#EFEAE0] p-7 hover:border-orion-orange hover:shadow-[0_18px_40px_rgba(44,17,57,0.12)] transition-all bg-white text-center"
                data-testid={`corp-pillar-${i}`}
              >
                <p.icon size={30} className="text-orion-orange mb-4 mx-auto group-hover:scale-110 transition-transform" strokeWidth={1.6} />
                <h3 className="font-serif text-xl text-orion-purple mb-3 leading-snug">{p.title}</h3>
                <p className="text-sm text-[#594A63] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ROI & BENEFITS BLOCK — corporate transparency about value */}
        <section id="roi" className="bg-[#2C1139] text-white">
          <div className="container-orion py-20 md:py-24">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">◈ Retorno sobre o Investimento</div>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight">
                Viagens que se pagam <em className="not-italic text-orion-gold">em resultados reais</em>.
              </h2>
              <p className="mt-5 text-white/80 text-base md:text-lg leading-relaxed">
                Cada roteiro corporativo é pensado para gerar retorno mensurável — em vendas, retenção, conexões estratégicas e capital humano.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="border border-white/10 bg-white/[0.03] backdrop-blur p-7 rounded-lg" data-testid="corp-roi-incentivo">
                <div className="text-[11px] uppercase tracking-[0.22em] text-orion-gold font-semibold mb-3">📈 Viagens de Incentivo</div>
                <h3 className="font-serif text-2xl mb-4">Premiação de Metas</h3>
                <ul className="space-y-3 text-sm text-white/85 leading-relaxed">
                  <li><strong className="text-white">Alavancagem de faturamento:</strong> a meta agressiva paga a viagem e sobra lucro.</li>
                  <li><strong className="text-white">Retenção de talentos:</strong> reduz turnover — substituir um talento qualificado custa caro.</li>
                  <li><strong className="text-white">Engajamento:</strong> quem volta motivado impacta clima e produtividade.</li>
                </ul>
              </div>
              <div className="border border-white/10 bg-white/[0.03] backdrop-blur p-7 rounded-lg" data-testid="corp-roi-negocios">
                <div className="text-[11px] uppercase tracking-[0.22em] text-orion-gold font-semibold mb-3">🤝 Negócios & Convenções</div>
                <h3 className="font-serif text-2xl mb-4">Feiras, Missões e Bleisure</h3>
                <ul className="space-y-3 text-sm text-white/85 leading-relaxed">
                  <li><strong className="text-white">Fechamento presencial:</strong> reunião olho-no-olho converte mais que videoconferência.</li>
                  <li><strong className="text-white">Prospecção qualificada:</strong> estar no polo do seu setor abre pipeline futuro.</li>
                  <li><strong className="text-white">Treinamento e inovação:</strong> conhecimento técnico traz novos produtos e otimizações.</li>
                </ul>
              </div>
            </div>
            <div className="bg-gradient-to-r from-orion-gold/15 via-orion-orange/8 to-transparent border-l-2 border-orion-gold px-5 py-4 rounded-r-lg">
              <p className="text-sm text-white/90 leading-relaxed">
                <strong className="text-orion-gold">Grupos a partir de 10 pessoas</strong> têm até <strong className="text-orion-gold">40% de desconto</strong> sobre os valores estimados. Nosso time monta o roteiro sob medida e a proposta comercial em até 24h úteis.
              </p>
            </div>
            <div className="text-center mt-10">
              <button
                onClick={() => setQuoteOpen(true)}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-orion-gold via-[#FFC470] to-orion-orange text-[#2C1139] px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:brightness-110 transition-all shadow-[0_14px_38px_rgba(255,179,71,0.45)] hover:-translate-y-0.5"
                data-testid="corp-roi-cta"
              >
                Solicitar Cotação Corporativa <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </section>

        {/* PORTFOLIO PREVIEW — brief summary of what /portfolio offers */}
        <section id="portfolio-preview" className="relative bg-gradient-to-br from-[#0d0526] via-[#180a3d] to-[#2C1139] text-white overflow-hidden">
          <span className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full bg-orion-gold/12 blur-3xl" aria-hidden />
          <span className="absolute -bottom-40 -right-32 w-[500px] h-[500px] rounded-full bg-[#38F0FF]/10 blur-3xl" aria-hidden />

          <div className="relative container-orion py-20 md:py-28 grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center max-w-6xl">
            <div>
              <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3 flex items-center gap-2">
                <Sparkles size={13} /> Catálogo Interativo
              </div>
              <h2 className="font-serif text-3xl md:text-5xl leading-[1.05]">
                Um portfólio pensado para <em className="not-italic text-orion-gold">quem decide</em>.
              </h2>
              <p className="mt-5 text-white/85 text-base md:text-lg leading-relaxed">
                O <strong>Portfólio Orion</strong> é um catálogo digital com <strong>112 destinos corporativos</strong> pré-selecionados, filtros por faixa de preço e setor, apresentação para o cliente sem valores expostos e exportação em PDF em 1 clique. Feito para você mostrar opções em qualquer reunião — pelo notebook ou pelo celular.
              </p>

              <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-white/90">
                {[
                  { icon: Briefcase, t: "112 destinos catalogados", d: "Corporativos, rodoviários, aéreos nacionais e internacionais." },
                  { icon: TrendingDown, t: "Filtros por faixa de preço", d: "Até R$ 5k · R$ 5-10k · Premium (10k+)." },
                  { icon: Users, t: "Setor & Público-alvo", d: "Tech, Vendas, Saúde, Família, Romântico e mais." },
                  { icon: ShieldCheck, t: "Modo Cliente", d: "Esconde preços com 1 toque para apresentar sem revelar margem." },
                  { icon: FileCheck2, t: "Exportar em PDF", d: "Catálogo A4 pronto para enviar por e-mail ou imprimir." },
                  { icon: ClipboardList, t: "Minha Lista salva", d: "Guarde favoritos com link compartilhável (deep-link)." },
                  { icon: Rocket, t: "Modo Apresentação", d: "Fullscreen com transições — perfeito para reuniões." },
                  { icon: Handshake, t: "Cotação Corporativa", d: "Multi-select de destinos → WhatsApp em 30 segundos." },
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`corp-portfolio-feat-${i}`}>
                    <span className="shrink-0 w-9 h-9 rounded-md border border-orion-gold/30 bg-orion-gold/10 flex items-center justify-center">
                      <f.icon size={16} className="text-orion-gold" strokeWidth={1.8} />
                    </span>
                    <span>
                      <strong className="text-white block leading-tight">{f.t}</strong>
                      <span className="text-white/70 text-[13px] leading-snug">{f.d}</span>
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-9 flex flex-wrap gap-3">
                <Link
                  to="/viagens-corporativas/portfolio"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-orion-gold via-[#FFC470] to-[#38F0FF] text-[#2C1139] px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:brightness-110 transition-all shadow-[0_14px_38px_rgba(255,179,71,0.35)] hover:-translate-y-0.5"
                  data-testid="corp-portfolio-open"
                >
                  Abrir Portfólio Completo <ArrowRight size={14} />
                </Link>
                <button
                  onClick={() => setQuoteOpen(true)}
                  className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:border-orion-orange hover:text-orion-orange transition-colors"
                  data-testid="corp-portfolio-quote"
                >
                  <ClipboardList size={14} /> Solicitar Cotação
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="relative overflow-hidden rounded-xl shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)] ring-1 ring-white/10">
                <img
                  src="https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/isnd35rc_Gemini_Generated_Image_cgawhccgawhccgaw.jpg"
                  alt="Portfólio Orion — catálogo interativo com 112 destinos corporativos"
                  loading="lazy"
                  decoding="async"
                  width="1440"
                  height="720"
                  className="w-full h-auto"
                  data-testid="corp-portfolio-preview-img"
                />
              </div>
              <div className="absolute -bottom-4 left-6 right-6 md:left-10 md:right-10 bg-[#2C1139] border border-orion-gold/40 backdrop-blur px-5 py-3 flex items-center justify-between gap-4 rounded-lg shadow-[0_18px_40px_rgba(0,0,0,0.5)]">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-orion-gold font-semibold">Preview ao vivo</div>
                  <div className="text-sm text-white/85 leading-tight">Explore agora — sem cadastro.</div>
                </div>
                <Link
                  to="/viagens-corporativas/portfolio"
                  className="text-[11px] uppercase tracking-[0.22em] font-bold text-orion-gold hover:text-white transition-colors flex items-center gap-1 whitespace-nowrap"
                  data-testid="corp-portfolio-mini-cta"
                >
                  Abrir <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* HIGHLIGHT — ÔNIBUS LEITO & HOTEL */}
        <section className="bg-[#2C1139] text-white overflow-hidden">
          <div className="container-orion py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Destaque</div>
              <h2 className="font-serif text-3xl md:text-5xl leading-[1.05]">
                Turismo com ônibus-leito no padrão premium.
              </h2>
              <p className="mt-5 text-white/80 text-base md:text-lg leading-relaxed">
                Deslocamos grupos com <strong>conforto de business class</strong> por terra. Poltronas reclináveis 180°, <strong>Wi-Fi Starlink de alta velocidade</strong>, tomadas, motorista especializado, monitoramento em tempo real e cronograma minutado. Ideal para roadshows regionais, retiros e integrações de equipe.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-white/85">
                {[
                  "Poltronas leito 180° e climatização por zona",
                  "Wi-Fi Starlink a bordo e tomadas em cada assento",
                  "Motorista adicional para trechos >7h — 100% legal (CTB art. 67)",
                  "Rastreamento GPS + coordenador via WhatsApp 24h",
                  "Kit de amenities e refeição a bordo (opcional)",
                ].map((li, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-orion-orange mt-1">›</span> {li}
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="relative overflow-hidden rounded-lg shadow-[0_18px_40px_rgba(0,0,0,0.35)]">
                <img src={BUS_IMG} alt="Frota Orion — ônibus-leito premium" loading="lazy" className="w-full aspect-[3/4] object-cover" />
                <span className="absolute inset-0 bg-gradient-to-t from-[#2C1139]/70 via-[#2C1139]/10 to-transparent" aria-hidden />
                <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-widest text-white/90 font-semibold">Frota Premium</span>
              </div>
              <div className="relative overflow-hidden rounded-lg shadow-[0_18px_40px_rgba(0,0,0,0.35)] mt-8">
                <img src={BUS_INTERIOR_IMG} alt="Interior ônibus-leito com Wi-Fi Starlink" loading="lazy" className="w-full aspect-[3/4] object-cover" />
                <span className="absolute inset-0 bg-gradient-to-t from-orion-orange/60 via-orion-orange/10 to-transparent" aria-hidden />
                <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-widest text-white font-semibold">Interior Leito · Wi-Fi Starlink</span>
              </div>
            </div>
          </div>
        </section>

        {/* PROCESS */}
        <section className="container-orion py-20 md:py-28">
          <div className="max-w-2xl mx-auto mb-12 text-center">
            <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Como trabalhamos</div>
            <h2 className="font-serif text-3xl md:text-5xl text-orion-purple leading-tight">
              Do briefing ao relatório final, em 4 etapas.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOW.map((h, i) => (
              <div key={i} className="relative p-7 border-l-4 border-orion-orange bg-[#FAF7F0] text-center" data-testid={`corp-step-${i}`}>
                <div className="font-serif text-4xl text-orion-orange/40 mb-3">{h.step}</div>
                <h3 className="font-serif text-xl text-orion-purple mb-2">{h.title}</h3>
                <p className="text-sm text-[#594A63] leading-relaxed">{h.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* SCHOOLS ATTENDED — reuses Portfolio's full-size marquee block */}
        <MarcasBlock
          id="escolas"
          eyebrow="◈ Escolas atendidas pela Orion"
          title="Instituições de ensino que viajam com a gente"
          subtitle="Colégios de fundamental, médio, técnico e universitário que confiam na curadoria pedagógica da Orion todo ano."
          logos={ESCOLAS_LOGOS.map((s) => ({ name: s.name, logo: s.src }))}
        />

        {/* FAQ */}
        <section className="bg-[#FAF7F0] py-20 md:py-28">
          <div className="container-orion max-w-4xl">
            <div className="text-center mb-14">
              <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Perguntas Frequentes</div>
              <h2 className="font-serif text-3xl md:text-5xl text-orion-purple leading-tight">
                Tudo o que uma diretoria precisa saber antes de contratar.
              </h2>
            </div>
            <div className="space-y-3" data-testid="corp-faq">
              {FAQ.map((item, i) => (
                <div key={i} className="border border-[#EFEAE0] bg-white overflow-hidden transition-all">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full flex items-start justify-between text-left p-5 md:p-6 gap-6 group"
                    data-testid={`corp-faq-q-${i}`}
                  >
                    <span className="font-serif text-base md:text-lg text-orion-purple leading-snug group-hover:text-orion-orange transition-colors">
                      {item.q}
                    </span>
                    <span className={`shrink-0 mt-1 text-orion-orange transition-transform ${openFaq === i ? "rotate-45" : ""}`}>
                      <ArrowRight size={20} strokeWidth={2} className="rotate-90" />
                    </span>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 md:px-6 pb-6 text-[15px] text-[#594A63] leading-relaxed" data-testid={`corp-faq-a-${i}`}>
                      {item.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative bg-gradient-to-br from-orion-orange via-[#E04828] to-[#B32E10] text-white overflow-hidden">
          <span className="absolute inset-0 opacity-20" aria-hidden style={{ backgroundImage: `url(${EVENT_IMG})`, backgroundSize: "cover" }} />
          <span className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" aria-hidden />
          <div className="relative container-orion py-20 md:py-28 text-center max-w-3xl">
            <Sparkles size={30} className="mx-auto text-white mb-5" />
            <h2 className="font-serif text-3xl md:text-5xl leading-tight">
              Vamos desenhar a sua próxima viagem corporativa?
            </h2>
            <p className="mt-5 text-white/90 text-base md:text-lg">
              Nosso especialista responde em minutos. Envie o briefing e receba uma proposta com fornecedores, cronograma e valores fechados.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setQuoteOpen(true)}
                className="inline-flex items-center gap-2 bg-white text-orion-purple px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-orion-cream transition-all hover:-translate-y-0.5 shadow-[0_14px_38px_rgba(0,0,0,0.25)]"
                data-testid="corp-cta-bottom-quote"
              >
                <ClipboardList size={16} /> Solicitar Cotação
              </button>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-white/10 transition-colors"
                data-testid="corp-cta-bottom"
              >
                <WhatsAppIcon size={16} color="#ffffff" /> WhatsApp
              </a>
              <Link
                to="/viagens-corporativas/portfolio"
                className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-white/10 transition-colors"
              >
                <Sparkles size={16} /> Experiências Corporativas
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      {quoteOpen && (
        <CorporateQuoteModal
          onClose={() => setQuoteOpen(false)}
          destinations={destinations}
          savedIds={[]}
          initial={{ preselectIds: [], focusCorp: true }}
        />
      )}
    </div>
  );
}
